import React from 'react';
import { useIntl } from 'react-intl';
import { getStartDate } from 'support/mediaTileHelper';
import { parseDate, formatAMPM } from 'common/support/dateHelper';

import { getUpcomingDay } from 'support/eventHelper';

import EventTitle from 'components/common/tile/EventTitle';
import AuthNWrapper, { WithAuthNProps } from 'components/hoc/AuthNWrapper';
import { useNetworkName } from 'hooks/kitchenSink';
import { ChannelKey, TileSize } from 'app-types';
import MediaTile from './MediaTile';
import BrandedAuthTile from './BrandedAuthTile';

import styles from './EventTile.module.scss';

const messages = {
  upcoming: {
    id: 'tile.upcoming',
    description: 'TTS for upcoming',
  },
  ttsAt: {
    id: 'textToSpeechHelper.at',
    description: 'TTS for at',
  },
};

export interface EventTileOwnProps {
  prefix?: string | null;
  title?: string | null;
  brand?: ChannelKey | null;
  startDate: number;
  src?: string | null;
  size?: TileSize | null;
  focusId?: string | number | null;
  className?: string | null;
  onClick?: (() => void) | null;
  onFocusSet?: (() => void) | null;
  autoSizeSrc?: boolean;
  isDvr?: boolean;
}

type EventTileProps = EventTileOwnProps & WithAuthNProps;

const EventTile = ({
  prefix = null,
  title = null,
  brand = null,
  authenticated = false,
  authTypes = [],
  startDate,
  ...rest
}: EventTileProps) => {
  const intl = useIntl();

  const date = formatAMPM(intl, new Date(startDate));

  const titleTts = [prefix, title].join(', '); // Pause between prefix and title instead of read out the 'dot'
  const brandTts = useNetworkName(brand);

  const upcomingTimeTts = [
    intl.formatMessage(messages.upcoming),
    getUpcomingDay(intl, new Date(startDate)),
    intl.formatMessage(messages.ttsAt),
    date,
  ].join(' ');

  const tts = [upcomingTimeTts, titleTts, brandTts].join(', ');

  const renderTitle = (hasFocus: boolean) => (
    <EventTitle
      prefix={prefix}
      title={title}
      brand={brand}
      shouldScroll={hasFocus}
    />
  );

  const renderMetadata = (hasFocus: boolean) => {
    const dateString = getStartDate(intl, parseDate(startDate));
    return (
      dateString && (
        <>
          <BrandedAuthTile
            hasFocus={hasFocus}
            brand={brand}
            authenticated={authenticated}
            authTypes={authTypes}
          />
          <div className={styles.startDate} data-testid="upcoming-time">
            {dateString}
          </div>
        </>
      )
    );
  };

  return (
    <MediaTile
      renderTitle={renderTitle}
      renderMetadata={renderMetadata}
      isLive
      ttsText={tts}
      {...rest}
    />
  );
};

export default React.memo(AuthNWrapper(EventTile));
